import _ from 'lodash'
import {RefElementBuilder} from '@wix/component-protocol/builder' // eslint-disable-line wix-editor/no-internal-import
import type {Builder, DalValue, ManifestFeature, Pointer} from '@wix/document-services-types'
import {ELEMENTS, type Action, type ElementItem} from '@wix/component-protocol/schema' // eslint-disable-line wix-editor/no-internal-import
import type {ElementBuilderData} from './ElementBuilderFactory'
import {isRefHostType} from '../../../../utils/refStructureUtils'

const BUILDER_COMPONENT_MAP: Record<string, string> = {
    'mobile.core.components.Container':
        'platform.builder.6a30caff-5dc3-4e4c-ba70-19db988f5830_1f0108be-06bb-4a81-9c55-a1cbd223926c',
    'wysiwyg.viewer.components.WRichText':
        'platform.builder.6a30caff-5dc3-4e4c-ba70-19db988f5830_4220f51c-7462-49f6-7352-14219c165b28',
    'wixui.StylableButton':
        'platform.builder.6a30caff-5dc3-4e4c-ba70-19db988f5830_4190f51c-3836-49f6-8114-96219c165b29',
    'wixui.ProGallery': 'platform.builder.6a30caff-5dc3-4e4c-ba70-19db988f5830_4190f51c-3836-49f6-8114-96219c165b31',
    'wixui.CollapsibleText':
        'platform.builder.6a30caff-5dc3-4e4c-ba70-19db988f5830_1392cc2d-20e7-411a-8b60-6e0af55b8ece',
    'wixui.FiveGridLine': 'platform.builder.6a30caff-5dc3-4e4c-ba70-19db988f5830_d2ff230a-71c3-4e58-8742-d68f69a2de0b',
    'wysiwyg.viewer.components.LinkBar':
        'platform.builder.6a30caff-5dc3-4e4c-ba70-19db988f5830_241afef1-74bd-4a10-843f-ac4928c25882',
    'wysiwyg.viewer.components.ExpandableMenu':
        'platform.builder.6a30caff-5dc3-4e4c-ba70-19db988f5830_3f27c3f4-5a2b-4b2c-9b14-05d9c0c6d5ec',
    'wysiwyg.viewer.components.MediaPlayer':
        'platform.builder.6a30caff-5dc3-4e4c-ba70-19db988f5830_8ec522c3-d8dd-43d8-9f07-1c4c1bb09d80',
    'wysiwyg.viewer.components.SiteButton':
        'platform.builder.6a30caff-5dc3-4e4c-ba70-19db988f5830_4190f51c-3836-49f6-8114-96219c165b29',
    'wixui.SlotsPlaceholder':
        'platform.builder.6a30caff-5dc3-4e4c-ba70-19db988f5830_5df938dd-345f-49bc-9623-aff6c9945efb',
    // Ref Component will be resolved in `toBuilderComponentType` method
    'wysiwyg.viewer.components.RefComponent': ''
}

export class BaseElementBuilder {
    private readonly supportedTypes = Object.keys(BUILDER_COMPONENT_MAP)
    private readonly customTypeMapper?: (elementType: string, compPointer: Pointer) => string | undefined
    private readonly type: string
    private readonly role: string
    private readonly actions: Record<string, Action>
    private readonly manifest?: ManifestFeature
    private readonly pointer: Pointer
    private readonly rootContainerPointer: Pointer
    private readonly data: DalValue
    private readonly elementsDefaults: Builder.ElementsDefaults

    constructor(data: ElementBuilderData) {
        this.customTypeMapper = data.customTypeMapper
        this.type = data.type
        this.role = data.role
        this.actions = data.actions
        this.manifest = data.manifest
        this.pointer = data.pointer
        this.rootContainerPointer = data.rootContainerPointer
        this.data = data.data
        this.elementsDefaults = data.elementsDefaults
    }

    static defaultBehaviors(): Builder.Behaviors {
        return {
            selectable: true,
            removable: true
        }
    }

    static defaultRootContainerBehaviors(): Builder.Behaviors {
        return {
            selectable: false,
            removable: false
        }
    }

    static toBuilderData(data: Builder.ElementsDefaults): Builder.DataItemOverrides {
        const metadataFields = ['id', 'type', 'metaData', 'rootCompId', 'pageId']
        return _(data)
            .omit(metadataFields)
            .mapValues(value => ({
                defaultValue: value
            }))
            .value()
    }

    getSelector(): string {
        return `[id$="${this.pointer.id}"]`
    }

    getBehaviors(): Builder.Behaviors {
        if (this.pointer.id === this.rootContainerPointer.id) {
            return BaseElementBuilder.defaultRootContainerBehaviors()
        }

        if (!this.manifest) {
            return BaseElementBuilder.defaultBehaviors()
        }
        return {
            selectable: this.manifest.behavior?.closed?.selectable ?? true,
            removable: !this.manifest.behavior?.preventHide
        }
    }

    getDisplayName(): string {
        return this.manifest?.displayName?.value || this.role
    }

    toBuilderComponentType(): string {
        let modifiedType = this.type
        // For manifest it might be helpful to have a custom type mapper
        if (this.customTypeMapper) {
            modifiedType = this.customTypeMapper(this.type, this.pointer) ?? this.type
        }

        const isRefHost = isRefHostType(modifiedType)
        // For the case when WIW we assign page
        // Then on WBS it's resolved to dev-center type of nested widget
        if (isRefHost) {
            return this.pointer.id
        }

        return BUILDER_COMPONENT_MAP[modifiedType] ?? modifiedType
    }

    // NOTE: Blocks builder support only refElement
    buildElement(): ElementItem | undefined {
        // Since Editor Elements CLI not support all types,
        // we ignore those that are not supported to avoid errors
        if (!this.supportedTypes.includes(this.type)) {
            return
        }

        const builder = new RefElementBuilder()

        const behaviors = this.getBehaviors()
        const componentType = this.toBuilderComponentType()
        const selector = this.getSelector()
        const displayName = this.getDisplayName()
        const dataOverrides = BaseElementBuilder.toBuilderData(this.data)

        const refElement = builder
            .setType(componentType)
            .setDisplayName(displayName)
            .setSelector(selector)
            .setBehaviors(behaviors)
            .setActions(this.actions)
            .setData(dataOverrides)
            .setElementsDefaults(this.elementsDefaults)
            .build()

        return {
            elementType: ELEMENTS.ELEMENT_TYPE.refElement,
            refElement
        }
    }
}
